import { useCallback } from 'react';
import { useToastUIStore } from '../../../../store/toastUIStore';
import { logger } from '../../../../utils/logger';

interface TaskErrorContext {
  operation: string;
  taskId?: string;
  topicId?: string;
  columnId?: string;
  additionalData?: Record<string, any>;
}

interface TaskErrorHandlerOptions {
  showUserNotification?: boolean;
  retryAction?: () => void;
  fallbackAction?: () => void;
}

/**
 * Standardized error handling hook for task operations
 * Provides consistent error logging, user notifications, and recovery actions
 */
export const useTaskErrorHandler = () => {
  const addToast = useToastUIStore(state => state.addToast);

  const handleError = useCallback((
    error: unknown,
    context: TaskErrorContext,
    options: TaskErrorHandlerOptions = {}
  ) => {
    const {
      showUserNotification = true,
      retryAction,
      fallbackAction
    } = options;

    // Standardize error information
    const errorMessage = error instanceof Error ? error.message : String(error);
    const errorInfo = {
      error: errorMessage,
      operation: context.operation,
      taskId: context.taskId,
      topicId: context.topicId,
      columnId: context.columnId,
      ...context.additionalData
    };

    // Always log the error with consistent format
    logger.error(`Task operation failed: ${context.operation}`, errorInfo);

    // Show user notification if enabled
    if (showUserNotification) {
      const userMessage = getUserFriendlyMessage(context.operation, errorMessage);
      
      addToast({
        type: 'error',
        title: 'Operation Failed',
        message: userMessage,
        duration: 5000,
        showRetry: !!retryAction,
        onRetry: retryAction,
        autoClose: true
      });
    }

    // Execute fallback action if provided
    if (fallbackAction) {
      try {
        fallbackAction();
      } catch (fallbackError) {
        logger.error('Fallback action failed', {
          originalError: errorMessage,
          fallbackError: fallbackError instanceof Error ? fallbackError.message : String(fallbackError),
          operation: context.operation
        });
      }
    }
  }, [addToast]);

  return { handleError };
};

/**
 * Convert technical operation names to user-friendly messages
 */
function getUserFriendlyMessage(operation: string, error: string): string {
  const operationMessages: Record<string, string> = {
    'create_task': 'Failed to create task. Please try again.',
    'update_task': 'Failed to update task. Your changes were not saved.',
    'delete_task': 'Failed to delete task. Please try again.',
    'move_task': 'Failed to move task. Please try again.',
    'reorder_task': 'Failed to reorder task. Please try again.',
    'assign_task': 'Failed to assign task. Please try again.',
    'voice_recording': 'Voice recording failed. Please try again.',
    'voice_processing': 'Failed to process voice message. Please try again.',
    'load_tasks': 'Failed to load tasks. Please refresh the page.',
    'load_columns': 'Failed to load columns. Please refresh the page.',
    'create_column': 'Failed to create column. Please try again.',
    'update_column': 'Failed to update column. Please try again.',
    'delete_column': 'Failed to delete column. Please try again.',
  };

  const userMessage = operationMessages[operation] || `Operation failed: ${operation}. Please try again.`;
  
  // Add specific error details for network issues
  if (error.toLowerCase().includes('network') || error.toLowerCase().includes('fetch')) {
    return `${userMessage} Please check your internet connection.`;
  }
  
  return userMessage;
}

/**
 * Pre-configured error handlers for common task operations
 */
export const useTaskErrorHandlers = () => {
  const { handleError } = useTaskErrorHandler();

  const handleTaskCreationError = useCallback((
    error: unknown,
    { topicId, columnId, title }: { topicId: string; columnId?: string; title?: string }
  ) => {
    handleError(error, {
      operation: 'create_task',
      topicId,
      columnId,
      additionalData: { title }
    });
  }, [handleError]);

  const handleTaskUpdateError = useCallback((
    error: unknown,
    { taskId, topicId, updates }: { taskId: string; topicId: string; updates?: Record<string, any> }
  ) => {
    handleError(error, {
      operation: 'update_task',
      taskId,
      topicId,
      additionalData: { updates }
    });
  }, [handleError]);

  const handleTaskDeletionError = useCallback((
    error: unknown,
    { taskId, topicId }: { taskId: string; topicId: string }
  ) => {
    handleError(error, {
      operation: 'delete_task',
      taskId,
      topicId
    });
  }, [handleError]);

  const handleTaskMoveError = useCallback((
    error: unknown,
    { taskId, topicId, fromColumn, toColumn }: { 
      taskId: string; 
      topicId: string; 
      fromColumn?: string; 
      toColumn?: string;
    }
  ) => {
    handleError(error, {
      operation: 'move_task',
      taskId,
      topicId,
      additionalData: { fromColumn, toColumn }
    });
  }, [handleError]);

  const handleVoiceError = useCallback((
    error: unknown,
    { topicId, columnId, operation }: { 
      topicId: string; 
      columnId?: string; 
      operation: 'voice_recording' | 'voice_processing';
    }
  ) => {
    handleError(error, {
      operation,
      topicId,
      columnId
    });
  }, [handleError]);

  const handleColumnError = useCallback((
    error: unknown,
    { columnId, topicId, operation }: { 
      columnId?: string; 
      topicId: string; 
      operation: 'create_column' | 'update_column' | 'delete_column';
    }
  ) => {
    handleError(error, {
      operation,
      columnId,
      topicId
    });
  }, [handleError]);

  return {
    handleTaskCreationError,
    handleTaskUpdateError,
    handleTaskDeletionError,
    handleTaskMoveError,
    handleVoiceError,
    handleColumnError,
    handleError // Generic handler for custom cases
  };
};